import { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
  Radio, RadioGroup, Stack, TextField, Typography,
} from '@mui/material';
import publicListingsService from '../../services/publicListingsService';
import { describeSearch } from './searchParser';
import { featureList, TYPE_OPTIONS } from './FilterRow';
import { MUST_HAVE } from './FiltersDrawer';
import { text } from '../../theme/colors';
import { INK } from './SearchHeader';

const money = (v) => `US$ ${Number(v).toLocaleString('en-US')}`;

/** The current filters as readable chips: "Harare", "Up to US$ 200,000", "3+ bed"… */
export function filterChips(values = {}) {
  const chips = [];
  if (values.q) chips.push(values.q);
  if (values.type) chips.push((TYPE_OPTIONS.find((o) => o.value === values.type) || {}).label);
  if (values.min_price && values.max_price) chips.push(`${money(values.min_price)} – ${money(values.max_price)}`);
  else if (values.max_price) chips.push(`Up to ${money(values.max_price)}`);
  else if (values.min_price) chips.push(`From ${money(values.min_price)}`);
  if (values.beds_min) chips.push(`${values.beds_min}+ bed`);
  if (values.baths_min) chips.push(`${values.baths_min}+ bath`);
  if (values.min_size) chips.push(`Stand ${Number(values.min_size).toLocaleString('en-US')} m²+`);
  featureList(values.features).forEach((f) => chips.push((MUST_HAVE.find(([k]) => k === f) || [f, f])[1]));
  if (values.listed_within) chips.push(`Listed in the last ${values.listed_within} days`);
  if (values.bbox) chips.push('Area on the map');
  return chips.filter(Boolean);
}

/**
 * Save the current search. We tell the buyer when a new verified home that
 * matches is published — nothing else, and they can stop it any time.
 */
export default function SaveSearchDialog({ open, values = {}, onClose, onSaved }) {
  const [name, setName] = useState('');
  const [channels, setChannels] = useState({ whatsapp: true, email: true });
  const [frequency, setFrequency] = useState('instant');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    const phrase = describeSearch(values);
    setName(phrase ? phrase.charAt(0).toUpperCase() + phrase.slice(1) : 'Homes for sale in Zimbabwe');
    setError('');
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const chips = filterChips(values);

  const save = async () => {
    setBusy(true); setError('');
    try {
      await publicListingsService.saveSearch({ name: name.trim(), filters: values, channels, frequency });
      onSaved?.();
    } catch (e) {
      setError(e.response?.data?.error || 'We could not save this search. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ pb: 0.5 }}>
        <Typography component="span" sx={{ display: 'block', fontSize: 26, fontWeight: 700, color: INK }}>Save this search</Typography>
        <Typography component="span" sx={{ display: 'block', fontSize: 15, color: text.secondary, mt: 0.5 }}>
          We&apos;ll tell you when a verified home that matches is published. Nothing else.
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1.5 }}>
          {chips.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {chips.map((c) => <Chip key={c} label={c} sx={{ fontWeight: 600, bgcolor: '#eef2f1' }} />)}
            </Box>
          )}
          {error && <Alert severity="error">{error}</Alert>}
          <TextField fullWidth label="Name this search" value={name} onChange={(e) => setName(e.target.value)} inputProps={{ maxLength: 120 }} />
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: 14, mb: 0.5 }}>Tell me on</Typography>
            <FormControlLabel control={<Checkbox checked={channels.whatsapp} onChange={(e) => setChannels((c) => ({ ...c, whatsapp: e.target.checked }))} />} label="WhatsApp" />
            <FormControlLabel control={<Checkbox checked={channels.email} onChange={(e) => setChannels((c) => ({ ...c, email: e.target.checked }))} />} label="Email" />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: 14, mb: 0.5 }}>How often</Typography>
            <RadioGroup value={frequency} onChange={(e) => setFrequency(e.target.value)}>
              <FormControlLabel value="instant" control={<Radio />} label="As soon as one is published" />
              <FormControlLabel value="daily" control={<Radio />} label="Once a day, in one message" />
            </RadioGroup>
          </Box>
          <Typography variant="body2" sx={{ color: text.secondary }}>
            Saved searches live under Saved homes on your dashboard. Stop them any time.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', color: INK, fontWeight: 600 }}>Cancel</Button>
        <Button onClick={save} disabled={busy || !name.trim() || (!channels.whatsapp && !channels.email)} variant="contained"
          sx={{ borderRadius: '10px', px: 3, textTransform: 'none', fontWeight: 700, bgcolor: INK, '&:hover': { bgcolor: '#0f172a' } }}>
          {busy ? 'Saving…' : 'Save search'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

